/** The value stored as the underlying Map key: the key itself, or a `keyFn` projection. */
type CacheKey<K> = K | string | number | object;

export type LRUObjectCacheOptions<K, V> = {
  /**
   * Projects a key to the underlying Map key for O(1) lookup. May return a
   * primitive canonical key or an object matched by reference identity (e.g.
   * `(doc) => doc.settings` to key by a sub-object). When provided, the
   * comparator scan is bypassed. The projection MUST be injective for the key
   * domain — two keys map to the same Map key iff they should be the same entry.
   */
  keyFn?: (key: K) => string | number | object;
  /** Cap on total retained bytes; LRU entries are evicted until within budget. Requires `sizeOf`. */
  maxBytes?: number;
  /** Approximate retained byte size of a value, used for the `maxBytes` budget. */
  sizeOf?: (value: V) => number;
};

/**
 * LRU cache for arbitrary keys. Recency is tracked by Map insertion order (the
 * first key is always least-recently-used), so eviction and recency refresh are
 * O(1) regardless of mode.
 *
 * Lookup has three modes, chosen at construction:
 * - `keyFn` provided: keys are projected to a Map key (primitive or object) and
 *   stored directly, giving O(1) `get`/`add`/`delete`.
 * - `compareKeys` only: O(n) scan calling the comparator per entry (for
 *   structural keys that can't cheaply project to a stable Map key).
 * - neither: keys are compared by Map identity (`SameValueZero`).
 */
export class LRUObjectCache<K, V> {
  private readonly cache: Map<CacheKey<K>, V> = new Map();
  private readonly keyFn?: (key: K) => string | number | object;
  private readonly maxBytes?: number;
  private readonly sizeOf?: (value: V) => number;
  private readonly useScan: boolean;
  private bytes: number = 0;
  public hits: number = 0;
  public misses: number = 0;

  constructor(
    private readonly size: number,
    private readonly compareKeys?: (a: K, b: K) => boolean,
    private readonly dispose: (value: V) => void = () => {},
    options?: LRUObjectCacheOptions<K, V>,
  ) {
    this.keyFn = options?.keyFn;
    this.maxBytes = options?.maxBytes;
    this.sizeOf = options?.sizeOf;
    this.useScan = compareKeys !== undefined && this.keyFn === undefined;
  }

  /** Number of entries currently retained. */
  get count(): number {
    return this.cache.size;
  }

  /** Approximate retained bytes (0 when no `sizeOf` was provided). */
  get approxBytes(): number {
    return this.bytes;
  }

  has(key: K): boolean {
    return !!this.get(key);
  }

  add(key: K, value: V) {
    const existing = this.useScan ? this.findScanKey(key) : this.resolveDirect(key);
    if (existing.found) {
      this.disposeAndDelete(existing.key);
    }

    this.cache.set(existing.key, value);
    if (this.sizeOf !== undefined) {
      this.bytes += this.sizeOf(value);
    }

    while (this.cache.size > this.size || this.overByteBudget()) {
      const oldest = this.cache.keys().next().value;
      if (oldest === undefined) {
        break;
      }
      this.disposeAndDelete(oldest);
    }
  }

  get(key: K): V | undefined {
    const resolved = this.useScan ? this.findScanKey(key) : this.resolveDirect(key);
    if (!resolved.found) {
      this.misses++;
      return undefined;
    }

    const entry = this.cache.get(resolved.key);
    if (entry !== undefined) {
      // Map iterates keys in insertion order, so deleting and re-inserting moves
      // this entry to the most-recently-used position.
      this.cache.delete(resolved.key);
      this.cache.set(resolved.key, entry);
    }
    this.hits++;
    return entry;
  }

  delete(key: K) {
    const existing = this.useScan ? this.findScanKey(key) : this.resolveDirect(key);
    if (existing.found) {
      this.disposeAndDelete(existing.key);
    }
  }

  clear() {
    for (const value of this.cache.values()) {
      this.dispose(value);
    }
    this.cache.clear();
    this.bytes = 0;
  }

  /** @deprecated Only to be used for special cases like when disposing an item is async */
  getAllValues(): V[] {
    return [...this.cache.values()];
  }

  private resolveDirect(key: K): { found: boolean; key: CacheKey<K> } {
    const mapKey = this.keyFn !== undefined ? this.keyFn(key) : key;
    return { found: this.cache.has(mapKey), key: mapKey };
  }

  /** Finds the stored key matching `key` via `compareKeys` (scan mode only). */
  private findScanKey(key: K): { found: boolean; key: CacheKey<K> } {
    for (const candidate of this.cache.keys()) {
      if (this.compareKeys !== undefined && this.compareKeys(candidate as K, key)) {
        return { found: true, key: candidate };
      }
    }
    return { found: false, key };
  }

  private disposeAndDelete(mapKey: CacheKey<K>): void {
    const value = this.cache.get(mapKey);
    if (value !== undefined) {
      if (this.sizeOf !== undefined) {
        this.bytes -= this.sizeOf(value);
      }
      this.dispose(value);
    }
    this.cache.delete(mapKey);
  }

  private overByteBudget(): boolean {
    // an entry larger than the whole budget is evicted right after being added
    return this.maxBytes !== undefined && this.bytes > this.maxBytes;
  }
}
